import React, { useEffect, useState } from 'react';
import { BookOpen } from 'lucide-react';
import ReaderPageLayout from '../layouts/ReaderPageLayout';

const WELCOME_API = 'https://v70pmk2q7a.execute-api.us-west-2.amazonaws.com/default/get-welcome';
const EMPTY_AUTHORS = [];

// Display legacy HTML bios as text without inserting author markup into the group.
function bioText(value, format) {
  if (typeof value !== 'string') return '';
  if (format === 'text' || !/<\/?[a-z][\s\S]*>/i.test(value)) return value;
  const document = new DOMParser().parseFromString(value, 'text/html');
  document.querySelectorAll('script, style, iframe, object').forEach(node => node.remove());
  document.querySelectorAll('br').forEach(node => node.replaceWith('\n'));
  document.querySelectorAll('p, div, li, h1, h2, h3').forEach(node => node.append('\n\n'));
  return document.body.textContent.trim();
}

const text = value => typeof value === 'string' ? value : '';
const records = value => Array.isArray(value) ? value.filter(item => item && typeof item === 'object' && !Array.isArray(item)) : [];
function authorLink(value, origin, image = false) {
  try {
    if (typeof value !== 'string' || !value.trim() || Array.from(value).some(c => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)) return null;
    const url = new URL(value, origin);
    return (image ? ['https:', 'http:'] : ['https:', 'http:', 'mailto:']).includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}
function normalizeBio(data, origin) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Missing author bio');
  return { contentFormat: data.contentFormat,
    welcomeHeader: text(data.welcomeHeader), tagline: text(data.tagline), welcomeMessage: text(data.welcomeMessage),
    profileImageUrl: authorLink(data.profileImageUrl, origin, true),
    sections: records(data.sections).map(section => ({title:text(section.title), text:text(section.text), imageUrl:authorLink(section.imageUrl, origin, true)})),
    ctaButtons: records(data.ctaButtons).map(button => ({label:text(button.label), url:authorLink(button.url, origin)})).filter(button => button.label && button.url),
  };
}
function BioImage({src, alt, className}) {
  const [failed, setFailed] = useState(false);
  return failed ? <p className="text-sm italic text-bookish-purple/80">Image unavailable</p> : <img src={src} alt={alt} className={className} loading="lazy" onError={() => setFailed(true)} />;
}
function directory(authors) {
  const seen = new Set();
  return records(authors).flatMap(author => {
    try {
      const url = new URL(author.url);
      if (!text(author.name).trim() || url.protocol !== 'https:' || url.username || url.password || url.port || !/^[a-z0-9]+(-[a-z0-9]+)*\.bookishcircle\.com$/.test(url.hostname) || seen.has(url.origin)) return [];
      seen.add(url.origin); return [{name:author.name, url:url.origin}];
    } catch { return []; }
  });
}

function AuthorBio({ author }) {
  const [bio, setBio] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let canceled = false;
    const timeout = setTimeout(() => { controller.abort(); if (!canceled) setError(true); }, 15000);
    setBio(null);
    setError(false);
    (async () => {
      try {
        const host = new URL(author.url).hostname;
        const response = await fetch(`${WELCOME_API}?host=${encodeURIComponent(host)}`, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to load author bio');
        const data = await response.json();
        const normalized = normalizeBio(data, author.url);
        if (!canceled && !controller.signal.aborted) setBio(normalized);
      } catch {
        if (!canceled) setError(true);
      } finally { clearTimeout(timeout); }
    })();
    return () => { canceled = true; clearTimeout(timeout); controller.abort(); };
  }, [author.url, retry]);

  return (
    <article aria-label={author.name} className="rounded-2xl border border-bookish-purple/20 bg-white p-6 sm:p-8 shadow-sm space-y-5">
      <h2 className="font-serif text-2xl sm:text-3xl break-words">{author.name}</h2>
      {!bio && !error && <p role="status">Loading {author.name}’s bio…</p>}
      {error && <p role="alert">This author’s bio could not be loaded. <button className="underline min-h-[44px]" onClick={() => setRetry(value => value + 1)}>Try again</button></p>}
      {bio && <>
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {bio.profileImageUrl && <BioImage key={bio.profileImageUrl} src={bio.profileImageUrl} alt={author.name} className="w-32 h-32 rounded-full object-cover border border-bookish-purple/20 shrink-0" loading="lazy" />}
          <div className="min-w-0 space-y-3">
            {bio.welcomeHeader && <h3 className="font-serif text-xl sm:text-2xl break-words">{bio.welcomeHeader}</h3>}
            {bio.tagline && <p className="text-lg break-words">{bio.tagline}</p>}
          </div>
        </div>
        <p className="whitespace-pre-wrap break-words leading-relaxed">{bioText(bio.welcomeMessage, bio.contentFormat) || 'This author has not added a bio yet.'}</p>
        {Array.isArray(bio.sections) && bio.sections.map((section, i) => <section key={i} className="border-t border-bookish-purple/15 pt-5 space-y-3">
          {section.title && <h3 className="font-serif text-xl break-words">{section.title}</h3>}
          {section.imageUrl && <BioImage key={section.imageUrl} src={section.imageUrl} alt={section.title || author.name} className="max-h-80 max-w-full rounded-xl object-contain" loading="lazy" />}
          {section.text && <p className="whitespace-pre-wrap break-words leading-relaxed">{bioText(section.text, bio.contentFormat)}</p>}
        </section>)}
        {Array.isArray(bio.ctaButtons) && <div className="flex flex-wrap gap-3">{bio.ctaButtons.map((button, i) => {
          const href = button.url && authorLink(button.url, author.url);
          return button.label && href ? <a key={i} href={href} className="rounded-lg border border-bookish-purple/20 px-4 py-2 text-bookish-purple underline">{button.label}</a> : null;
        })}</div>}
      </>}
      <a href={author.url} className="inline-block rounded-full bg-bookish-purple text-white px-5 py-3 hover:bg-bookish-purpleAccent">Visit {author.name}’s shop</a>
    </article>
  );
}

export default function GroupAuthors({ authors = EMPTY_AUTHORS }) {
  const entries = directory(authors);
  return (
    <div className="bg-bookish-cream text-bookish-royal min-h-screen"><ReaderPageLayout title="Our Authors" description="Meet the storytellers behind your next bookish adventure." centeredHeader>
      <div className="flex flex-col items-center text-center mb-8 gap-3"><BookOpen size={32} aria-hidden="true" className="text-bookish-purple" /><p>Pull up a chair, discover their stories, and step into each author’s shop.</p><a href="/bookstore" className="underline min-h-[44px] inline-flex items-center">Explore our books</a></div>
      {entries.length ? <div className="space-y-8">{entries.map(author => <AuthorBio key={author.url} author={author} />)}</div>
        : <p className="text-center">Our authors will appear here once they are connected. Please check back soon.</p>}
    </ReaderPageLayout></div>
  );
}
